import { Column } from 'exceljs';

// IMPORTANT : Members of DossierSheetRow should also be add to the AllDashBoardColulmnsToDisplay list
export type DossierSheetRow = {
  ID?: string;
  fitterBrandId?: string;
  fitterBrand?: string;
  fitterId?: string;
  creationDate?: Date | string | null;
  customerLastname?: string;
  customerFirstname?: string;
  customerEmail?: string;
  sinisterDate?: Date | string | null;
  licensePlateNumber?: string;
  vehicleBrand?: string;
  vehicleModel?: string;
  sinisterGlassClaim?: string;
  contractNumber?: string;
  insurer?: string;
  origin?: string;
  rriNumber?: string;
  customerLastnameContract?: string;
  customerFirstnameContract?: string;
  customerEmailContract?: string;
  outsideIntervention?: string; // yes/no
  replacementVehicle?: string; // yes/no
  operationType?: string;
  conveying?: string; // yes/no
  calibration?: string; // yes/no/notSpecified
  sentToBill?: string; // yes/no
  resources?: string; // list with `,` separator
  interventionCity?: string;
  fitterCity?: string;
  apptDate?: Date | string | null;
  apptHour?: Date | string | null;
  agentLastname?: string;
  agentFirstname?: string;
  agentEmail?: string;
  finishLaterComment?: string;
  abandonmentComment?: string;
  apmtCancelComment?: string;
  noShow?: boolean;
  abandonDate?: Date | string | null;
  globalOrigin?: string;
  businessOrigin?: string;
  phoneNumber?: string;
  sinisterNumber?: string;
  marketingDataAccepted?: string; // yes/no
  lastStep?: string;
  abTestPhone?: string; // yes/no
};

export type DossierManagersSheetRow = Record<string, number> & {
  agentLastname?: string;
  agentFirstname?: string;
  agentEmail?: string;
  date?: Date | string | null;
  repair?: number;
  replace?: number;
  interventionAtGarage?: number;
  interventionAtHome?: number;
  requestAtGarage?: number;
  requestAtHome?: number;
};

export type PartnerSheetRow = {
  id?: string;
  internalId?: string;
  siret?: string;
  brand?: string;
  city?: string;
  brandId?: string;
  address?: string;
  cp?: string;
  phone?: string;
  email?: string;
  lat?: number;
  lon?: number;
  fitterType: string;
};

export type SmsStatsSheetRow = {
  fitterId?: string;
  nbCustomerSms?: number;
  nbFitterSms?: number;
  total?: number;
};

export type SuperAdminSmsStatsByConfSheetRow = {
  from?: string;
  to?: string;
  smsType?: string;
  nbSms?: number;
  nbCharacters?: number;
  nbSmsFactured?: number;
};

export type SuperAdminStatsByConfSheetRow = {
  confId?: string;
  nbInsurerApmt?: number;
  nbCustomerApmt?: number;
  nbActiveInsurerAccounts?: number;
  nbBrandFitters?: number;
  nbAppointments?: number;
  conversionRateB2C?: number; // 1.0 = +100%
  conversionRateB2B?: number; // 1.0 = +100%
  repairRate?: number; // 1.0 = +100%
  variationFactor?: number; // 1.0 = +100%
};

export type NpsSheetRow = {
  journeyType?: string;
  clientName?: string;
  dossierNumber?: string;
  date?: Date | string | null;
  result?: number | null;
  filledFields?: string;
  userSkipped?: string;
  appOrigin?: string;
};

export type AbTestingRow = {
  testType: string;
  nbDossiersA: number;
  nbDossiersB: number;
  rate: number;
};

export type AnySheetRow =
  | DossierSheetRow
  | DossierManagersSheetRow
  | PartnerSheetRow
  | SmsStatsSheetRow
  | SuperAdminStatsByConfSheetRow
  | SuperAdminSmsStatsByConfSheetRow
  | NpsSheetRow
  | AbTestingRow;

export type SpecificSheetColumnName<T extends AnySheetRow> = keyof T;

export type DossierSheetColumnName = SpecificSheetColumnName<DossierSheetRow>;

export type DossierManagersSheetColumnName =
  SpecificSheetColumnName<DossierManagersSheetRow>;

export type PartnerSheetColumnName = SpecificSheetColumnName<PartnerSheetRow>;

export type SuperAdminStatsByConfSheetColumnName =
  SpecificSheetColumnName<SuperAdminStatsByConfSheetRow>;

export type SuperAdminSmsStatsByConfSheetColumnName =
  SpecificSheetColumnName<SuperAdminSmsStatsByConfSheetRow>;

export type NpsSheetColumnName = SpecificSheetColumnName<NpsSheetRow>;

export type AbTestingSheetColumnName = SpecificSheetColumnName<AbTestingRow>;

export type SpecificSheetColumn<R extends AnySheetRow> = Partial<Column> & {
  key: SpecificSheetColumnName<R>;
  header: string;
};

export type DossierSheetColumn = SpecificSheetColumn<DossierSheetRow>;

export type DossierManagersSheetColumn =
  SpecificSheetColumn<DossierManagersSheetRow>;

export type PartnerSheetColumn = SpecificSheetColumn<PartnerSheetRow>;

export type SmsStatsSheetColumn = SpecificSheetColumn<SmsStatsSheetRow>;

export type SuperAdminSmsStatsSheetColumn =
  SpecificSheetColumn<SuperAdminSmsStatsByConfSheetRow>;

export type SuperAdminStatsByConfSheetColumn =
  SpecificSheetColumn<SuperAdminStatsByConfSheetRow>;

export type NpsSheetColumn = SpecificSheetColumn<NpsSheetRow>;

export type AbTestingSheetColumn = SpecificSheetColumn<AbTestingRow>;

export const AllDashBoardColulmnsToDisplay: DossierSheetColumnName[] = [
  'ID',
  'fitterBrandId',
  'fitterBrand',
  'fitterId',
  'creationDate',
  'customerLastname',
  'customerFirstname',
  'customerEmail',
  'sinisterDate',
  'licensePlateNumber',
  'vehicleBrand',
  'vehicleModel',
  'sinisterGlassClaim',
  'contractNumber',
  'insurer',
  'customerLastnameContract',
  'customerFirstnameContract',
  'customerEmailContract',
  'outsideIntervention',
  'replacementVehicle',
  'operationType',
  'conveying',
  'calibration',
  'sentToBill',
  'resources',
  'interventionCity',
  'fitterCity',
  'apptDate',
  'apptHour',
  'agentLastname',
  'agentFirstname',
  'agentEmail',
  'finishLaterComment',
  'abandonmentComment',
  'apmtCancelComment',
  'noShow',
  'abandonDate',
  'sinisterNumber',
  'marketingDataAccepted',
  'lastStep',
];

export const InsurerDashBoardColulmnsToDisplay: DossierSheetColumnName[] = [
  'ID',
  'rriNumber',
  'fitterBrandId',
  'fitterBrand',
  'fitterId',
  'creationDate',
  'customerLastname',
  'customerFirstname',
  'customerEmail',
  'sinisterDate',
  'licensePlateNumber',
  'vehicleBrand',
  'vehicleModel',
  'sinisterGlassClaim',
  'contractNumber',
  'insurer',
  'customerLastnameContract',
  'customerFirstnameContract',
  'customerEmailContract',
  'outsideIntervention',
  'replacementVehicle',
  'operationType',
  'interventionCity',
  'fitterCity',
  'apptDate',
  'apptHour',
  'agentLastname',
  'agentFirstname',
  'agentEmail',
  'finishLaterComment',
  'abandonmentComment',
  'apmtCancelComment',
  'abandonDate',
  'sinisterNumber',
  'marketingDataAccepted',
  'lastStep',
];
